// Client for the Bindarr Companion API (contract v1, /api/companion).
//
// Every call goes through the app's global fetch, so the session bearer token
// and 401 handling apply unchanged. Errors carry the server's {error, code}
// plus the HTTP status so screens can tell "stale" (409) from "not available"
// (503) from "not yours" (404) without string-matching messages.

const BASE = '/api/companion';

export class CompanionError extends Error {
  constructor(message, { status = 0, code = null, data = null } = {}) {
    super(message);
    this.name = 'CompanionError';
    this.status = status;
    this.code = code;
    this.data = data;
  }
}

// Idempotency ids for actions and requests. The server replays a repeated
// action_id with the same payload and refuses a changed one, so a retry after a
// dropped connection can never clone a copy.
export function newId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  // Old WebViews without randomUUID: still unique enough for an idempotency key.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}-${Math.random().toString(36).slice(2, 12)}`;
}

async function call(path, { method = 'GET', body, signal } = {}) {
  const res = await fetch(BASE + path, {
    method,
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal
  });
  let data = null;
  try { data = await res.json(); } catch { data = null; }
  if (!res.ok) {
    throw new CompanionError(data?.error || `Request failed (${res.status})`, { status: res.status, code: data?.code || null, data });
  }
  return data;
}

// Only the six context keys the contract defines, only when they carry a value.
export function cleanContext(ctx) {
  const out = {};
  if (!ctx) return out;
  const num = (v) => (v === null || v === undefined || v === '' ? undefined : Number(v));
  if (Number.isFinite(num(ctx.entry_id))) out.entry_id = num(ctx.entry_id);
  if (typeof ctx.set_id === 'string' && ctx.set_id) out.set_id = ctx.set_id;
  if (Number.isFinite(num(ctx.location_id))) out.location_id = num(ctx.location_id);
  if (Number.isFinite(num(ctx.compartment_id))) out.compartment_id = num(ctx.compartment_id);
  if (Number.isFinite(num(ctx.page))) out.page = num(ctx.page);
  if (typeof ctx.opening_id === 'string' && ctx.opening_id) out.opening_id = ctx.opening_id;
  // Dashboard game view ('pokemon' | 'mtg' | ...): scopes a general ask.
  if (typeof ctx.game === 'string' && /^[a-z0-9_-]{1,32}$/i.test(ctx.game)) out.game = ctx.game;
  // Scanner companion context (freeform-scanner-contract.md): forwarded as an
  // object of ids/versions; the backend re-verifies all of it.
  if (ctx.scanner && typeof ctx.scanner === 'object' && ctx.scanner.active === true) out.scanner = JSON.parse(JSON.stringify(ctx.scanner));
  return out;
}

export const companionApi = {
  capabilities: () => call('/capabilities'),

  // input_mode 'voice' makes the server treat the request as read-only: it may
  // return a proposed action, never execute one. Text may execute exact edits.
  // `history` (optional, read-only for the server) carries recent voice
  // transcript so a short spoken follow-up still has its subject.
  // Contract v2: one structured turn per request. conversation_id is stable
  // per thread, previous_request_id chains turns, history is read-only.
  // retry_of_request_id (additive): set when this is the identical re-send of
  // an errored request under the same request_id; the server may ignore it.
  ask: ({ message, context, request_id = newId(), input_mode = 'text', history, conversation_id, previous_request_id, context_snapshot_id, retry_of_request_id, selection, signal }) =>
    call('/ask', {
      method: 'POST',
      body: {
        request_id, message, context: cleanContext(context), input_mode,
        ...(history ? { history } : {}),
        ...(conversation_id ? { conversation_id } : {}),
        ...(previous_request_id ? { previous_request_id } : {}),
        ...(context_snapshot_id ? { context_snapshot_id } : {}),
        ...(retry_of_request_id ? { retry_of_request_id } : {}),
        ...(selection ? { selection } : {})
      },
      signal
    }).then(r => ({ ...r, request_id: r?.request_id || request_id, turn_id: r?.turn_id || r?.request_id || request_id })),

  cancel: (request_id) => call(`/requests/${encodeURIComponent(request_id)}/cancel`, { method: 'POST', body: {} }),

  getEntry: (entry_id) => call(`/entries/${encodeURIComponent(entry_id)}`),

  // One physical copy, one patch, one receipt. expected_revision comes from
  // getEntry() or a previous action; the server answers 409 if the row moved.
  act: ({ entry_id, expected_revision, patch, action_id = newId() }) =>
    call('/actions', { method: 'POST', body: { action_id, entry_id: Number(entry_id), expected_revision, patch } }),

  receipts: () => call('/receipts'),

  // :id is the receipt/action being reversed; the body carries a fresh
  // idempotency id for the undo itself.
  undo: (receipt_id, action_id = newId()) =>
    call(`/actions/${encodeURIComponent(receipt_id)}/undo`, { method: 'POST', body: { action_id } }),

  // Scanner voice corrections (scanner-voice-contract.md): exact committed copy, then one finish write.
  scannerTarget: (scan_commit_id, entry_id) => call(`/scanner/commits/${encodeURIComponent(scan_commit_id)}/entries/${encodeURIComponent(entry_id)}`),
  scannerFinish: ({ action_id, scan_commit_id, entry_id, expected_revision, printing }) =>
    call('/scanner/finish', { method: 'POST', body: { action_id, scan_commit_id, entry_id: Number(entry_id), expected_revision, printing } }),
  scannerIdentity: ({ action_id, scan_commit_id, entry_id, expected_revision, card_id }) =>
    call('/scanner/identity', { method: 'POST', body: { action_id, scan_commit_id, entry_id: Number(entry_id), expected_revision, card_id } }),
  scannerSets: (game, q) => call(`/scanner/sets?game=${encodeURIComponent(game || 'pokemon')}&q=${encodeURIComponent(q || '')}`),
  scannerOwned: (card_id, printing) => call(`/scanner/owned?card_id=${encodeURIComponent(card_id)}${printing ? `&printing=${encodeURIComponent(printing)}` : ''}`),
  discovery: (game) => call(`/discovery${game ? `?game=${encodeURIComponent(game)}` : ''}`),
  quiz: () => call('/quiz'),
  completion: (set_id) => call(`/completion/${encodeURIComponent(set_id)}`),

  // Organize recount sessions (api-contract.md): explicit count session; every
  // capture is an observation with a stable id; adjust sets the absolute count
  // or moves the bound observation to another finish; only receipts announce.
  recounts: () => call('/recounts'),
  startRecount: ({ action_id = newId(), game, set_id } = {}) => call('/recounts', { method: 'POST', body: { action_id, ...(game ? { game } : {}), ...(set_id ? { set_id } : {}) } }),
  recount: (id) => call(`/recounts/${encodeURIComponent(id)}`),
  // `selection` answers COUNT_SELECTION_REQUIRED: { expected_members_hash, missing_entry_ids } chosen by the user.
  recountObserve: (id, { action_id = newId(), observation_id, card_id, printing, quantity, scope_entry_id, candidates, selection }) =>
    call(`/recounts/${encodeURIComponent(id)}/observe`, { method: 'POST', body: { action_id, observation_id, card_id, printing, ...(quantity != null ? { quantity: Number(quantity) } : {}), ...(scope_entry_id != null ? { scope_entry_id: Number(scope_entry_id) } : {}), ...(Array.isArray(candidates) && candidates.length ? { candidates: candidates.slice(0, 8) } : {}), ...(selection ? { selection } : {}) } }),
  recountAdjust: (id, { action_id = newId(), observation_id, quantity, printing, card_id, scope_entry_id, selection }) =>
    call(`/recounts/${encodeURIComponent(id)}/adjust`, { method: 'POST', body: { action_id, observation_id, ...(quantity != null ? { quantity: Number(quantity) } : {}), ...(printing ? { printing } : {}), ...(card_id ? { card_id } : {}), ...(scope_entry_id != null ? { scope_entry_id: Number(scope_entry_id) } : {}), ...(selection ? { selection } : {}) } }),
  finishRecount: (id, { action_id = newId() } = {}) => call(`/recounts/${encodeURIComponent(id)}/finish`, { method: 'POST', body: { action_id } }),

  openings: () => call('/openings'),
  createOpening: ({ title, pack_count, cost, currency, notes, action_id = newId() }) =>
    call('/openings', { method: 'POST', body: { action_id, title, pack_count, cost, currency, notes } }),
  openingCommand: (body) => call('/opening-command',{method:'POST',body}),
  openingPack: (id, body) => call(`/openings/${encodeURIComponent(id)}/packs`, {method:'POST',body}),
  packArtwork: (body, signal) => call('/pack-artwork', {method:'POST',body,signal}),
  packRecognize: (body,signal) => call('/pack-recognize',{method:'POST',body,signal}),
  packUndo: (id,action_id=newId()) => call(`/pack-saves/${encodeURIComponent(id)}/undo`,{method:'POST',body:{action_id}}),
  openingVision: (body) => call('/opening-vision',{method:'POST',body}),
  opening: (id) => call(`/openings/${encodeURIComponent(id)}`),
  finishOpening: (id, { notes, action_id = newId() } = {}) =>
    call(`/openings/${encodeURIComponent(id)}/finish`, { method: 'POST', body: { action_id, notes } }),

  voiceSession: ({ context, sdp }) =>
    call('/voice/session', { method: 'POST', body: { context: cleanContext(context), sdp } })
};

export default companionApi;
